import React from "react";

import { useNavigate } from "react-router-dom";

import { useClient } from "../../context/ClientContext";

import SafeIcon from "../../common/SafeIcon";

import {
  FiBriefcase,
  FiUser,
  FiShield,
  FiZap,
  FiArrowRight,
} from "react-icons/fi";

/*
 * =========================================================
 * PROFITYO CLIENT DASHBOARD
 *
 * Dedicated Client Portal page.
 *
 * - Displays the active client organization
 * - Displays membership and account information
 * - Provides access to Client AI Copilot
 * - No firm administration controls
 *
 * ClientLayout handles the sidebar and header.
 * =========================================================
 */

export default function ClientDashboard() {
  const {
    activeWorkspace,
    activeClient,
    profile,
    user,
  } = useClient();

  const navigate = useNavigate();

  const clientName =
    activeWorkspace?.name ||
    activeClient?.name ||
    "Your Organization";

  const displayName = [
    profile?.first_name,
    profile?.last_name,
  ]
    .filter(Boolean)
    .join(" ");

  const membershipRole =
    activeWorkspace?.role ||
    "client";

  return (
    <div className="mx-auto max-w-7xl">

      {/* WELCOME SECTION */}

      <div className="mb-8">

        <p className="text-xs font-bold uppercase tracking-widest text-emerald-700">
          Client Workspace
        </p>

        <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-900 md:text-4xl">
          Welcome
          {displayName
            ? `, ${displayName}`
            : ""}
        </h2>

        <p className="mt-3 text-sm leading-7 text-slate-600">
          Access your business workspace,
          review your organization information,
          and use ProfitYo AI Copilot to ask
          questions about your available
          accounting records.
        </p>
      </div>

      {/* OVERVIEW CARDS */}

      <div className="grid gap-6 lg:grid-cols-3">

        {/* ORGANIZATION CARD */}

        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-2">

          <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700">
            <SafeIcon
              icon={FiBriefcase}
              className="text-2xl"
            />
          </div>

          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
            Your Organization
          </p>

          <h3 className="mt-2 break-words text-2xl font-black text-slate-900">
            {clientName}
          </h3>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">

            {/* WORKSPACE TYPE */}

            <div className="rounded-2xl bg-slate-50 p-4">

              <p className="text-xs text-slate-500">
                Workspace Type
              </p>

              <p className="mt-2 text-sm font-bold text-slate-800">
                Client Organization
              </p>
            </div>

            {/* MEMBERSHIP ROLE */}

            <div className="rounded-2xl bg-slate-50 p-4">

              <p className="text-xs text-slate-500">
                Membership Role
              </p>

              <p className="mt-2 text-sm font-bold capitalize text-slate-800">
                {membershipRole.replaceAll(
                  "_",
                  " "
                )}
              </p>
            </div>

            {/* CURRENCY */}

            <div className="rounded-2xl bg-slate-50 p-4">

              <p className="text-xs text-slate-500">
                Currency
              </p>

              <p className="mt-2 text-sm font-bold text-slate-800">
                {activeWorkspace?.currency ||
                  "Not specified"}
              </p>
            </div>

            {/* COUNTRY */}

            <div className="rounded-2xl bg-slate-50 p-4">

              <p className="text-xs text-slate-500">
                Country
              </p>

              <p className="mt-2 text-sm font-bold text-slate-800">
                {activeWorkspace?.country ||
                  "Not specified"}
              </p>
            </div>
          </div>
        </section>

        {/* ACCOUNT CARD */}

        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">

          <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">

            <SafeIcon
              icon={FiUser}
              className="text-2xl"
            />
          </div>

          <h3 className="text-lg font-black text-slate-900">
            Your Account
          </h3>

          <div className="mt-5 space-y-5">

            <div>
              <p className="text-xs text-slate-500">
                Name
              </p>

              <p className="mt-1 break-words text-sm font-semibold text-slate-900">
                {displayName ||
                  "Not provided"}
              </p>
            </div>

            <div>
              <p className="text-xs text-slate-500">
                Email
              </p>

              <p className="mt-1 break-all text-sm font-semibold text-slate-900">
                {user?.email ||
                  "Unavailable"}
              </p>
            </div>

            <div className="flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-3 text-xs font-semibold text-emerald-800">
              <SafeIcon icon={FiShield} />
              Verified workspace membership
            </div>
          </div>
        </section>
      </div>

      {/* AI COPILOT FEATURE CARD */}

      <section className="mt-6 overflow-hidden rounded-3xl border border-violet-100 bg-white shadow-sm">

        <div className="flex flex-col gap-5 p-6 md:flex-row md:items-center md:justify-between md:p-8">

          <div className="flex min-w-0 items-start gap-4">

            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-violet-100 text-violet-700">
              <SafeIcon
                icon={FiZap}
                className="text-2xl"
              />
            </div>

            <div className="min-w-0">

              <div className="flex flex-wrap items-center gap-3">

                <h3 className="text-lg font-black text-slate-900">
                  ProfitYo AI Copilot
                </h3>

                <span className="rounded-full bg-emerald-50 px-3 py-1 text-[10px] font-bold text-emerald-700">
                  Available
                </span>
              </div>

              <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-600">
                Ask questions about your available
                transactions, chart of accounts,
                and business finances. ProfitYo AI
                works with the selected organization's
                authorized records and explains
                when additional information is needed.
              </p>

              <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">

                <SafeIcon
                  icon={FiShield}
                  className="text-emerald-700"
                />

                Organization-specific AI access
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() =>
              navigate("/client/ai")
            }
            className="flex shrink-0 items-center justify-center gap-2 rounded-xl bg-violet-600 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-violet-700"
          >
            Open AI Copilot

            <SafeIcon
              icon={FiArrowRight}
            />
          </button>

        </div>
      </section>

      {/* PORTAL INFORMATION */}

      <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">

        <h3 className="text-lg font-black text-slate-900">
          Your Client Portal
        </h3>

        <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-600">
          Your organization is connected to your
          ProfitYo account. Additional
          client-specific financial dashboards,
          documents, transactions, and reports
          will become available as those
          features are implemented.
        </p>

        <p className="mt-4 text-xs font-medium text-slate-500">
          Accounting firm administration tools
          are separate from your Client Portal.
        </p>
      </section>
    </div>
  );
}